import type { T_Maybe } from '@tryiris-ai/js-types';
import { toBase64FromId } from '#~/api/index.js';
import type { ByteId } from '#~/api/index.js';

/**
 * Gets the notification subject from a reservation.
 *
 * @param input - The reservation to get the state id from.
 * @param input.agentId - The agent id.
 * @param input.reservationId - The reservation id.
 * @param input.workerId - The worker id.
 * @returns The state id.
 */
export const autoTrackingNotificationSubjectWithReservation = (input: {
  agentId?: T_Maybe<ByteId>;
  reservationId?: T_Maybe<ByteId>;
  workerId?: T_Maybe<ByteId>;
}): T_Maybe<string> => {
  const { agentId, reservationId, workerId } = input;

  const agentIdHex = toBase64FromId(agentId);
  const reservationIdHex = toBase64FromId(reservationId);
  const workerIdHex = toBase64FromId(workerId);

  if (!agentIdHex || !reservationIdHex || !workerIdHex) {
    return undefined;
  }

  return `agent.${agentIdHex}.autotracking.${reservationIdHex}.${workerIdHex}.tracking`;
};

/**
 * Gets the state id from a reservation.
 *
 * @param input - The reservation to get the state id from.
 * @param input.agentId - The agent id.
 * @param input.reservationId - The reservation id.
 * @param input.workerId - The worker id.
 * @returns The state id.
 */
export const stateIdWithReservation = (input: {
  agentId: T_Maybe<ByteId>;
  reservationId: T_Maybe<ByteId>;
  workerId: T_Maybe<ByteId>;
}): T_Maybe<string> => {
  const { agentId, reservationId, workerId } = input;

  const agentIdHex = toBase64FromId(agentId);
  const reservationIdHex = toBase64FromId(reservationId);
  const workerIdHex = toBase64FromId(workerId);

  if (!agentIdHex || !reservationIdHex || !workerIdHex) {
    return undefined;
  }

  return `agent.${agentIdHex}.autotracking.${reservationIdHex}.${workerIdHex}.state`;
};
